import { SHOP_MAP } from 'constant/shop';

export type CashSettlementCurrency =
  'JPY' | 'HKD' | 'SGD' | 'AED' | 'EUR' | 'USD';

export type CashDenomination = {
  denomination: string;
  label: string;
};

type CashSettlementDenominations = {
  bills: CashDenomination[];
  coins: CashDenomination[];
};

const JAPANESE_BILLS: CashDenomination[] = [
  { denomination: '10000', label: '10,000円札' },
  { denomination: '5000', label: '5,000円札' },
  { denomination: '2000', label: '2,000円札' },
  { denomination: '1000', label: '1,000円札' },
];

const JAPANESE_COINS: CashDenomination[] = [
  { denomination: '500', label: '500円玉' },
  { denomination: '100', label: '100円玉' },
  { denomination: '50', label: '50円玉' },
  { denomination: '10', label: '10円玉' },
  { denomination: '5', label: '5円玉' },
  { denomination: '1', label: '1円玉' },
];

const HK_BILLS: CashDenomination[] = [
  { denomination: '1000', label: 'HK$1,000' },
  { denomination: '500', label: 'HK$500' },
  { denomination: '100', label: 'HK$100' },
  { denomination: '50', label: 'HK$50' },
  { denomination: '20', label: 'HK$20' },
  { denomination: '10', label: 'HK$10' },
];

const HK_COINS: CashDenomination[] = [
  { denomination: '5', label: 'HK$5' },
  { denomination: '2', label: 'HK$2' },
  { denomination: '1', label: 'HK$1' },
  { denomination: '0.5', label: '50¢' },
  { denomination: '0.2', label: '20¢' },
  { denomination: '0.1', label: '10¢' },
];

const SGD_BILLS: CashDenomination[] = [
  { denomination: '1000', label: 'S$1,000' },
  { denomination: '100', label: 'S$100' },
  { denomination: '50', label: 'S$50' },
  { denomination: '10', label: 'S$10' },
  { denomination: '5', label: 'S$5' },
  { denomination: '2', label: 'S$2' },
];

const SGD_COINS: CashDenomination[] = [
  { denomination: '1', label: 'S$1' },
  { denomination: '0.5', label: '50¢' },
  { denomination: '0.2', label: '20¢' },
  { denomination: '0.1', label: '10¢' },
  { denomination: '0.05', label: '5¢' },
];

const AED_BILLS: CashDenomination[] = [
  { denomination: '1000', label: 'AED 1,000' },
  { denomination: '500', label: 'AED 500' },
  { denomination: '200', label: 'AED 200' },
  { denomination: '100', label: 'AED 100' },
  { denomination: '50', label: 'AED 50' },
  { denomination: '20', label: 'AED 20' },
  { denomination: '10', label: 'AED 10' },
  { denomination: '5', label: 'AED 5' },
];

const AED_COINS: CashDenomination[] = [
  { denomination: '1', label: 'AED 1' },
  { denomination: '0.5', label: '50 fils' },
  { denomination: '0.25', label: '25 fils' },
];

const EUR_BILLS: CashDenomination[] = [
  { denomination: '500', label: '€500' },
  { denomination: '200', label: '€200' },
  { denomination: '100', label: '€100' },
  { denomination: '50', label: '€50' },
  { denomination: '20', label: '€20' },
  { denomination: '10', label: '€10' },
  { denomination: '5', label: '€5' },
];

const EUR_COINS: CashDenomination[] = [
  { denomination: '2', label: '€2' },
  { denomination: '1', label: '€1' },
  { denomination: '0.5', label: '50¢' },
  { denomination: '0.2', label: '20¢' },
  { denomination: '0.1', label: '10¢' },
  { denomination: '0.05', label: '5¢' },
  { denomination: '0.02', label: '2¢' },
  { denomination: '0.01', label: '1¢' },
];

const USD_BILLS: CashDenomination[] = [
  { denomination: '100', label: '$100' },
  { denomination: '50', label: '$50' },
  { denomination: '20', label: '$20' },
  { denomination: '10', label: '$10' },
  { denomination: '5', label: '$5' },
  { denomination: '1', label: '$1' },
];

const USD_COINS: CashDenomination[] = [
  { denomination: '1', label: '$1' },
  { denomination: '0.5', label: '50¢' },
  { denomination: '0.25', label: '25¢' },
  { denomination: '0.1', label: '10¢' },
  { denomination: '0.05', label: '5¢' },
  { denomination: '0.01', label: '1¢' },
];

export const getCashSettlementCurrencyByShop = (
  shopId?: number,
): CashSettlementCurrency => {
  switch (shopId) {
    case SHOP_MAP.HONGKONG:
      return 'HKD';
    case SHOP_MAP.SINGAPORE:
    case SHOP_MAP.SINGAPORE_ASU_GST:
    case SHOP_MAP.SINGAPORE_GX:
      return 'SGD';
    case SHOP_MAP.DUBAI:
      return 'AED';
    case SHOP_MAP.FRANCE:
      return 'EUR';
    case SHOP_MAP.NEW_YORK:
      return 'USD';
    case SHOP_MAP.GINZA:
    case SHOP_MAP.UMEDA:
    default:
      return 'JPY';
  }
};

export const getCashSettlementDenominations = (
  currency: CashSettlementCurrency,
): CashSettlementDenominations => {
  switch (currency) {
    case 'HKD':
      return { bills: HK_BILLS, coins: HK_COINS };
    case 'SGD':
      return { bills: SGD_BILLS, coins: SGD_COINS };
    case 'AED':
      return { bills: AED_BILLS, coins: AED_COINS };
    case 'EUR':
      return { bills: EUR_BILLS, coins: EUR_COINS };
    case 'USD':
      return { bills: USD_BILLS, coins: USD_COINS };
    case 'JPY':
    default:
      return { bills: JAPANESE_BILLS, coins: JAPANESE_COINS };
  }
};

export const getCashSettlementCurrencySymbol = (
  currency: CashSettlementCurrency,
): string => {
  switch (currency) {
    case 'HKD':
      return 'HK$';
    case 'SGD':
      return 'S$';
    case 'AED':
      return 'AED';
    case 'EUR':
      return '€';
    case 'USD':
      return '$';
    case 'JPY':
    default:
      return '¥';
  }
};

export const getCashSettlementInputPrecision = (
  currency: CashSettlementCurrency,
): number => (currency === 'JPY' ? 0 : 2);

export const formatCashSettlementAmount = (
  amount: number | null | undefined,
  currency: CashSettlementCurrency,
): string => {
  const safeAmount = amount ?? 0;

  switch (currency) {
    case 'HKD':
      return safeAmount.toLocaleString('en-HK', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    case 'SGD':
      return safeAmount.toLocaleString('en-SG', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    case 'AED':
      return safeAmount.toLocaleString('en-AE', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    case 'EUR':
      return safeAmount.toLocaleString('en-IE', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    case 'USD':
      return safeAmount.toLocaleString('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    case 'JPY':
    default:
      return safeAmount.toLocaleString('ja-JP');
  }
};

export const formatInputAmount = (
  value: string | number | undefined,
): string => {
  if (value === undefined || value === null || value === '') return '';

  return `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
};

export const parseInputAmount = (
  value: string | undefined,
  currency: CashSettlementCurrency,
): number => {
  if (!value) return 0;
  const parsed =
    getCashSettlementInputPrecision(currency) === 0
      ? value.replace(/[^\d]/g, '')
      : value.replace(/[^\d.]/g, '');
  return parsed ? Number(parsed) : 0;
};
